var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import React, { useCallback } from 'react';
import { translate } from '@utils/i18n';
import { Accordion, Text, showSuccessToast, showErrorToast } from '@ui';
import DetailSection from '@components/v2/DetailSection';
import RelatedItems from '@components/v2/RelatedItems';
import { Loader, LoaderSizes } from '@components/Loader/Loader';
import { InlineEdit } from '@components/v2/InlineEdit';
import KeyValueList from '@components/v2/KeyValueList';
import { namespace } from '../../constants';
import useDeficiencyDetail from '../../../hooks/useDeficiencyDetail';
import useUpdateDeficiencyDetail from '../../../hooks/useUpdateDeficiency';
import { getAssetsInfo, getGeneralInfo } from './getGeneralInfo';
export default function DetailInfo({ id, fullWidthGeneralInfo = false }) {
    const { updateDeficiency } = useUpdateDeficiencyDetail();
    const handleContentChange = useCallback((field, value) => __awaiter(this, void 0, void 0, function* () {
        if (value === '--')
            return;
        try {
            yield updateDeficiency.mutateAsync({
                id,
                deficiencyUpdate: { [field]: value }
            });
            showSuccessToast(translate('update_success', { namespace }));
        }
        catch (_a) {
            showErrorToast(translate('error_message_updating', { namespace }));
        }
    }), [id, updateDeficiency]);
    const { data: deficiency, isLoading, error } = useDeficiencyDetail(id);
    if (isLoading) {
        return React.createElement(Loader, { size: LoaderSizes.large });
    }
    if (error || !deficiency) {
        return React.createElement("div", null, translate('def_error_message', { namespace }));
    }
    const assetsInfo = getAssetsInfo(deficiency);
    const hasAssetsInfo = assetsInfo.length > 0;
    const generalInfoColumnClass = fullWidthGeneralInfo || !hasAssetsInfo ? 'span12' : 'span6';
    const assetsInfoColumnClass = fullWidthGeneralInfo
        ? 'span12 detail-info__assets-full-width'
        : 'span6 detail-info__assets-half-width';
    return (React.createElement(React.Fragment, null,
        React.createElement(DetailSection, { title: translate('tabs.detail.related', { namespace }) },
            React.createElement(RelatedItems, { items: deficiency.relatedItems.map((item) => ({
                    label: item.name,
                    labelClass: item.icon,
                    items: item.items.map((relatedItem) => ({
                        display: relatedItem.label,
                        path: relatedItem.path
                    }))
                })) })),
        React.createElement(DetailSection, { title: translate('tabs.detail.general_information', { namespace }) },
            React.createElement("div", { className: "row-fluid" },
                React.createElement("div", { className: generalInfoColumnClass },
                    React.createElement("div", { className: "row-fluid" },
                        React.createElement(KeyValueList, { items: getGeneralInfo(deficiency), onEdit: (field, value) => {
                                void handleContentChange(field, value);
                            } })),
                    React.createElement("div", { className: "row-fluid", style: { padding: '10px 0 10px 0' } },
                        React.createElement(Accordion, { title: "Internal Notes" },
                            React.createElement(InlineEdit, { variant: "x-full", placeholder: "Enter text...", style: { whiteSpace: 'pre-wrap' }, onBlur: (e) => {
                                    const value = e.currentTarget.textContent || '';
                                    if (value !== deficiency.internalNote)
                                        void handleContentChange('internalNote', value);
                                } }, deficiency.internalNote))),
                    React.createElement("div", { className: "row-fluid", style: { padding: '10px 0 10px 0' } },
                        React.createElement(Accordion, { title: "Notes" },
                            React.createElement(InlineEdit, { variant: "x-full", placeholder: "Enter text...", style: { whiteSpace: 'pre-wrap' }, onBlur: (e) => {
                                    const value = e.currentTarget.textContent || '';
                                    if (value !== deficiency.note)
                                        void handleContentChange('note', value);
                                } }, deficiency.note))),
                    deficiency.aiPhotoSummary && (React.createElement("div", { className: "row-fluid", style: { padding: '10px 0 10px 0' } },
                        React.createElement(Accordion, { title: "AI Photo Assessment", defaultExpanded: true },
                            React.createElement("div", { style: { display: 'flex', gap: 6, alignItems: 'flex-start', padding: '4px 0' } },
                                React.createElement("i", { className: "fa-light fa-robot", style: { fontSize: 12, marginTop: 2, flexShrink: 0 } }),
                                React.createElement(Text, { variant: "body", as: "p", style: { margin: 0, fontStyle: 'italic' } }, deficiency.aiPhotoSummary))))),
                    (deficiency === null || deficiency === void 0 ? void 0 : deficiency.isResolved) && (React.createElement("div", { className: "row-fluid", style: { padding: '10px 0 10px 0' } },
                        React.createElement(Accordion, { title: "Resolution Notes", defaultExpanded: true },
                            React.createElement(InlineEdit, { variant: "x-full", placeholder: "Enter text...", style: { whiteSpace: 'pre-wrap' }, onBlur: (e) => {
                                    const value = e.currentTarget.textContent || '';
                                    if (value !== deficiency.resolutionNotes)
                                        void handleContentChange('resolutionNotes', value);
                                } }, deficiency.resolutionNotes))))),
                hasAssetsInfo && (React.createElement("div", { className: assetsInfoColumnClass, style: { paddingLeft: '18px', border: '1px solid #ccc' } },
                    React.createElement(KeyValueList, { items: assetsInfo })))))));
}
